import { ReferencedEntitiesPills } from "@/components/ReferencedEntitiesPills";
import { cn } from "@/utils";
import { Message } from "@cohesive/api-client";
import { Entity, RailStep } from "@cohesive/ui-components";
import { ChevronDown, Clock, MessageSquare, Sparkles } from "lucide-react";
import { AnimatePresence, cubicBezier, motion } from "motion/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ChatBubble from "../ChatBubble";
import DefaultMessageRenderer from "../ChatBubble/renderers/DefaultMessageRenderer";
import { dedupeToolCallsByIndex, ToolCalls } from "../ToolCall/ToolCall";
import {
  deriveCanvasAddSummary,
  shouldShowCanvasAddSummary,
} from "./canvasAddSummary";
import { CanvasNodesAddedSummary } from "./CanvasNodesAddedSummary";
import { CollapsableThinkingBlock } from "./CollapsableThinkingBlock";
import { isAnswerComplete } from "./messageStatus";

/**
 * One conversation turn (a user prompt + the assistant's reply) grouped by the
 * API's `turn_id`. The activity that produced the answer — auto-selected
 * skills, thinking, tool calls, and any intermediate replies — renders inside a
 * single vertical "rail" (one connecting line through the icon gutter), so
 * every return type during the turn reads consistently. Completed turns
 * collapse that rail to a one-line summary ("5 steps · 12s"); the in-flight
 * turn stays expanded and streams live. Only the final text answer stays
 * visible below the rail (activity-only collapse).
 */
interface TurnViewProps {
  /** Messages sharing one `turn_id` (user first, then assistant rounds). */
  turn: Message[];
  entities: Map<string, Entity>;
  isLastTurn: boolean;
  /**
   * Whether the flow session is idle (`ready`). Threaded down from ChatArea
   * rather than subscribed to here, so the live turn's canvas-add summary can
   * wait for the agent to stop working without costing one session
   * subscription per turn.
   */
  isSessionReady?: boolean;
  lastUserMessageId?: string;
  lastUserMessageRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Wall-clock span of a turn: the full distance from its earliest timestamp to
 * its latest, across every timestamp field of every message. Folding in all of
 * created_at / updated_at / sent_at means a real turn always resolves to a
 * number; `undefined` only survives the impossible case of no parseable
 * timestamp anywhere in the turn.
 */
function turnDurationMs(turn: Message[]): number | undefined {
  let start = Infinity;
  let end = -Infinity;
  for (const m of turn) {
    for (const ts of [m.created_at, m.updated_at, m.sent_at]) {
      const t = ts ? Date.parse(ts) : NaN;
      if (Number.isNaN(t)) continue;
      start = Math.min(start, t);
      end = Math.max(end, t);
    }
  }
  if (start === Infinity) return undefined;
  return Math.max(0, end - start);
}

/** Earliest timestamp in a turn — the anchor for the live "working for" timer. */
function turnStartMs(turn: Message[]): number | undefined {
  let start = Infinity;
  for (const m of turn) {
    for (const ts of [m.created_at, m.updated_at, m.sent_at]) {
      const t = ts ? Date.parse(ts) : NaN;
      if (!Number.isNaN(t)) start = Math.min(start, t);
    }
  }
  return start === Infinity ? undefined : start;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

// Design system "ease-out" — cubic-bezier(0.2, 0, 0, 1), the default curve for
// UI motion (cohesive-design-system theme.css `--ease-out`).
const EASE_OUT = cubicBezier(0.2, 0, 0, 1);

// How each chain-of-thought step pops into the rail: the design system's
// content-reveal — fade + an 8px rise — at dur-mid (200ms) / ease-out. This is
// the "State / panel-expand" register, not the 800ms atmospheric one (reserved
// for hero beats). COT steps stream in over the socket one at a time, so they
// self-stagger; no artificial per-item delay is added. Spread onto each step's
// motion.div.
const STEP_ENTER = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.2, ease: EASE_OUT },
};

// How often (ms) the COT re-reads a streaming turn. See useThrottledValue.
const TURN_THROTTLE_MS = 250;

/**
 * Throttle a rapidly-changing value to at most one update per `windowMs`
 * (leading + trailing edge).
 *
 * A burst of websocket events can rewrite a turn several times within a frame or
 * two — new message → its content → its status — and rendering each transient
 * in-between state is what makes the COT flicker (the rail collapsing/expanding,
 * the "Working…"/"Waiting…" tail or "Planning…" header flipping, the answer
 * darting between the rail and below it). Coalescing those into ~`windowMs`
 * steps removes the flicker while still advancing steadily during continuous
 * streaming. A plain trailing debounce can't be used here: while deltas keep
 * arriving it would never settle, freezing the COT until the stream paused.
 */
function useThrottledValue<T>(value: T, windowMs: number): T {
  const [snapshot, setSnapshot] = useState(value);
  const lastUpdateRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const elapsed = Date.now() - lastUpdateRef.current;
    const commit = () => {
      lastUpdateRef.current = Date.now();
      setSnapshot(value);
    };
    if (elapsed >= windowMs) {
      commit();
    } else {
      // Fallback for when updates stop mid-window; superseded by the leading
      // edge of the next burst if more arrive first.
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(commit, windowMs - elapsed);
    }
    return () => clearTimeout(timerRef.current);
  }, [value, windowMs]);

  return snapshot;
}

export function TurnView({
  turn: rawTurn,
  entities,
  isLastTurn,
  isSessionReady = false,
  lastUserMessageId,
  lastUserMessageRef,
}: TurnViewProps) {
  // Coalesce bursts of websocket updates into ~10fps so transient intermediate
  // states don't flash the COT (see useThrottledValue). Everything below derives
  // from this settled snapshot rather than the raw, per-event prop.
  const turn = useThrottledValue(rawTurn, TURN_THROTTLE_MS);

  const userMsg = turn.find((m) => m.origin === "user");
  const assistantMsgs = useMemo(
    () =>
      turn.filter(
        (m) => m.origin === "assistant" || m.origin === "assistant-tool-call",
      ),
    [turn],
  );
  const skills = userMsg?.auto_selected_skills ?? [];

  // Canvas nodes THIS turn added, announced at the foot of the turn that made
  // the edits. Derived from the throttled snapshot so a streaming turn doesn't
  // re-derive on every websocket event.
  const canvasAddSummary = useMemo(() => deriveCanvasAddSummary(turn), [turn]);

  // The final answer is the LAST plain-assistant text message (origin
  // "assistant"). Its content stays visible below the rail (activity-only
  // collapse). Content carried on an `assistant-tool-call` message is
  // pre-tool-call narration — not the answer — so it ALWAYS renders as a rail
  // message step inside the COT, never below the rail.
  //
  // Keying off origin (not merely "last with content") is what keeps this
  // stable while streaming. Over the websocket a message that ends up calling a
  // tool first streams as origin "assistant" with content, then flips to
  // "assistant-tool-call" once its tool-arg deltas arrive. Selecting "last with
  // content" made that narration masquerade as the final answer at the bottom
  // of the COT for the whole run, only snapping into the rail once the real
  // answer finished. Requiring origin === "assistant" classifies it as a rail
  // step the moment its origin flips, so it reads in the COT consistently
  // whether the turn is in-flight or done.
  const answerMsg = useMemo(
    () =>
      [...assistantMsgs]
        .reverse()
        .find((m) => m.origin === "assistant" && !!m.content),
    [assistantMsgs],
  );

  const stepCount = useMemo(() => {
    let n = skills.length;
    for (const m of assistantMsgs) {
      if (m.thinking_content) n += 1;
      if (m.id !== answerMsg?.id && m.content) n += 1;
      // Count what actually renders: dedupe transient duplicate tool calls (the
      // same logical call re-sent with a different id at the same index) so the
      // header doesn't briefly read one step too high, then drop (e.g. 5 → 4).
      n += dedupeToolCallsByIndex(m.tool_calls)?.length ?? 0;
    }
    return n;
  }, [skills, assistantMsgs, answerMsg]);

  // "Past collapsed, live expanded": the in-flight turn streams expanded for its
  // WHOLE duration and only collapses once the final answer settles. Syncing
  // `open` on `live` gives that auto-collapse while still honouring a manual
  // toggle afterwards.
  //
  // A turn is finished only once its FINAL answer settles: the last
  // plain-assistant text message (origin "assistant") must also be the LAST
  // message in the turn AND have reached done/processed. Position matters — an
  // intermediate assistant reply can be `processed` while the turn continues
  // with more tool-call rounds, so completion can't be inferred from any settled
  // assistant message (that prematurely collapsed the COT after the first
  // reply), only from the final one. A terminal failure/cancellation on the last
  // message also ends the turn. Everything else — user message only, streaming,
  // mid-flight tool calls, awaiting a tool confirmation — keeps the turn live so
  // the COT stays open across the full turn.
  const lastTurnMsg = turn[turn.length - 1];
  const finalAnswerSettled =
    !!answerMsg &&
    answerMsg.id === lastTurnMsg?.id &&
    isAnswerComplete(answerMsg.status);
  const terminated =
    lastTurnMsg?.status === "failed" || lastTurnMsg?.status === "cancelled";
  const isComplete = finalAnswerSettled || terminated;
  const live = isLastTurn && !isComplete;

  // Past turns can't grow any more, so their count is final and always shows.
  // The live turn waits for the session to go idle: while it is `processing`,
  // further canvas-edit calls can still land in this turn, and a count rendered
  // early would visibly grow under the reader. This deliberately does not
  // require a final answer message — a tool-only turn never produces one, and
  // those turns still need to announce their edits.
  const showCanvasAddSummary = shouldShowCanvasAddSummary({
    isLastTurn,
    isSessionReady,
  });

  // While live, tick once a second so the header time counts up in real time;
  // once the turn settles we freeze to the recorded wall-clock span.
  const startMs = useMemo(() => turnStartMs(turn), [turn]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [live]);

  // Header summary: step count + elapsed time — "working for" (counting up)
  // while in progress, "worked for" once done. Before the first token streams
  // back (live, no steps yet) the header instead reads "Planning out work…",
  // then snaps into the step/duration summary as soon as the model responds.
  const summary = useMemo(() => {
    if (live && stepCount === 0) return "Planning out work…";
    const steps = `${stepCount} step${stepCount === 1 ? "" : "s"}`;
    const ms = live
      ? startMs != null
        ? Math.max(0, nowMs - startMs)
        : undefined
      : turnDurationMs(turn);
    if (ms == null) return steps;
    return `${steps} · ${live ? "working for" : "worked for"} ${formatDuration(ms)}`;
  }, [stepCount, live, startMs, nowMs, turn]);

  const [open, setOpen] = useState(live);
  useEffect(() => setOpen(live), [live]);

  // Show the rail header the instant a turn goes live — even before any step
  // streams back — so the clock/summary line is present immediately (reading
  // "Planning out work…"). Past turns with no steps stay header-less (just the
  // answer below).
  const hasRail = stepCount > 0 || live;
  // The header only toggles open/closed when there's nested activity to reveal;
  // during the "planning" window there's nothing to expand.
  const canToggle = stepCount >= 1;
  // With the header acting as the rail's top node, even one nested step is
  // worth a connecting line down from it.
  const showLine = open && stepCount >= 1;

  // "Planning out work…" phase: live, but the model hasn't produced its first
  // step yet, so the header itself is the heartbeat (shimmered to feel alive).
  const planning = live && stepCount === 0;

  // Trailing heartbeat: once there's at least one step, keep a pulsing
  // "Working…" step pinned at the foot of the rail for the entire live turn, so
  // there's always a visible "still going" signal — including while a step is
  // streaming or a tool is mid-run. (Before the first step the header itself is
  // the "Planning out work…" heartbeat, so it only kicks in at stepCount >= 1.)
  const showWorkingTail = live && stepCount >= 1;

  // …unless the turn is parked on a tool call awaiting the user's approval. Then
  // the agent isn't working — it's the user's move — so the heartbeat reads
  // "Waiting for user input…" in the amber `lc-waiting` lifecycle colour and
  // slows down (calmer, less "busy" cadence) to match.
  const awaitingApproval =
    lastTurnMsg?.origin === "assistant-tool-call" &&
    (lastTurnMsg.tool_calls?.some(
      (tc) => tc.user_confirmation_status === "pending",
    ) ??
      false);

  return (
    <div>
      {userMsg && (
        <ChatBubble
          message={userMsg}
          entities={entities}
          ref={
            userMsg.id === lastUserMessageId ? lastUserMessageRef : undefined
          }
        >
          {userMsg.referenced_entities &&
            userMsg.referenced_entities.length > 0 && (
              <div className="w-full flex justify-end mt-1">
                <ReferencedEntitiesPills
                  entities={userMsg.referenced_entities}
                  className="px-0 py-0 max-w-[90%] justify-end flex-wrap"
                />
              </div>
            )}
        </ChatBubble>
      )}

      {hasRail && (
        <div className="relative">
          {/* The rail line runs from the header's Clock down through the nested
              steps. Every icon (header + thinking/tool/skill steps) centres on
              x≈28px and masks it with its own bg-card backing. */}
          {showLine && (
            <span
              aria-hidden
              className="pointer-events-none absolute left-[27px] top-5 bottom-5 w-px bg-n-20"
            />
          )}

          {/* Persistent disclosure header — click the same row to toggle; the
              activity nests directly beneath it. */}
          <button
            type="button"
            onClick={canToggle ? () => setOpen((o) => !o) : undefined}
            aria-expanded={canToggle ? open : undefined}
            disabled={!canToggle}
            className={cn(
              "group flex w-full items-center gap-2 px-4 py-2 text-left outline-none",
              !canToggle && "cursor-default",
            )}
          >
            <span className="relative z-[1] inline-flex size-6 shrink-0 items-center justify-center bg-background text-n-80">
              <Clock className="size-[18px]" />
            </span>
            <span
              className={cn(
                "min-w-0 flex-1 truncate font-sans text-sm text-n-60 transition-colors duration-150 ease-out",
                canToggle && "group-hover:text-n-80",
                // Shimmer the "Planning out work…" label so the very first
                // moment of a turn reads as alive (mirrors the running
                // tool-call title shimmer).
                planning && "shimmer",
              )}
            >
              {summary}
            </span>
            {canToggle && (
              <ChevronDown
                className={cn(
                  "size-3.5 shrink-0 text-n-50 transition-all duration-150 ease-out group-hover:text-n-70",
                  open && "rotate-180",
                )}
              />
            )}
          </button>

          {/* Quick, non-disruptive collapse of the activity into the single
              header row — and the symmetric expand on re-open — using the
              design system's height/opacity collapse (200ms easeInOut, per
              HomeActivityFeed). `initial={false}` skips the animation on the
              turn's first paint so a freshly-started turn doesn't flash open;
              auto-collapse at turn end and manual toggles both animate.
              overflow-hidden clips the body while its height animates. */}
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                key="cot-body"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                {skills.length > 0 && (
                  <div className="px-4 py-2">
                    {skills.map((skill) => (
                      <motion.div key={skill.id} {...STEP_ENTER}>
                        <RailStep glyph={Sparkles} tone="tinted" last>
                          <div className="pt-1 font-sans text-sm text-n-70">
                            Using skill:{" "}
                            <span className="font-medium text-n-90">
                              {skill.name}
                            </span>
                          </div>
                        </RailStep>
                      </motion.div>
                    ))}
                  </div>
                )}

                {assistantMsgs.map((m) => (
                  <React.Fragment key={m.id}>
                    {m.thinking_content != null &&
                      m.thinking_content !== "" && (
                        <motion.div {...STEP_ENTER}>
                          <CollapsableThinkingBlock
                            content={m.thinking_content}
                            isStreaming={
                              m.status === "streaming" &&
                              m.thinking_duration_milliseconds == null
                            }
                            thinkingDurationMilliseconds={
                              m.thinking_duration_milliseconds
                            }
                          />
                        </motion.div>
                      )}
                    {/* Intermediate assistant text (a reply before the final
                      answer): a message step showing the full prose inline —
                      not auto-collapsed like thinking. The final answer renders
                      below the rail instead. */}
                    {m.id !== answerMsg?.id && m.content && (
                      <motion.div className="px-4 py-2" {...STEP_ENTER}>
                        <RailStep
                          icon={<MessageSquare className="size-[18px]" />}
                          last
                        >
                          {/* Step-weight prose (text-sm) matching the other rail
                            steps; zero the leading/trailing paragraph margins so
                            the first line sits level with the glyph. */}
                          <div className="-ml-1 font-sans text-sm text-n-70 text-pretty [&_p:first-child]:mt-0 [&_p:last-child]:mb-0">
                            <DefaultMessageRenderer
                              message={m}
                              entities={entities}
                            />
                          </div>
                        </RailStep>
                      </motion.div>
                    )}
                    <ToolCalls message={m} />
                  </React.Fragment>
                ))}

                {/* Trailing heartbeat for the live turn — fades in/out so it
                    doesn't pop as steps come and go. A radar-pulse dot in the
                    gutter (masking the rail line) plus a shimmered label keep it
                    feeling active. While the turn is parked awaiting the user's
                    approval it turns amber, slows its pulse/shimmer, and reads
                    "Waiting for user input…" instead of "Working…". */}
                <AnimatePresence initial={false}>
                  {showWorkingTail && (
                    <motion.div
                      key="cot-working-tail"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                      className="px-4 py-2"
                    >
                      <RailStep
                        icon={
                          <span className="relative flex size-2.5 items-center justify-center">
                            <span
                              className={cn(
                                "absolute inline-flex size-2.5 animate-ping rounded-full motion-reduce:animate-none",
                                awaitingApproval
                                  ? "bg-lc-waiting [animation-duration:2.6s]"
                                  : "bg-rust",
                              )}
                            />
                            <span
                              className={cn(
                                "relative inline-flex size-2 rounded-full",
                                awaitingApproval ? "bg-lc-waiting" : "bg-rust",
                              )}
                            />
                          </span>
                        }
                        last
                      >
                        <span
                          className="shimmer -ml-1 font-sans text-sm text-n-60"
                          style={
                            awaitingApproval
                              ? ({
                                  // Amber, slower sweep for the "your turn" state.
                                  "--shimmer-duration": "3s",
                                  "--shimmer-bg-color":
                                    "color-mix(in oklab, var(--lc-waiting) 65%, var(--n-40))",
                                  "--shimmer-highlight-color":
                                    "var(--lc-waiting)",
                                } as React.CSSProperties)
                              : undefined
                          }
                        >
                          {awaitingApproval
                            ? "Waiting for user input…"
                            : "Working…"}
                        </span>
                      </RailStep>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Canvas additions this turn made, announced at the foot of the turn —
          directly above its final answer when there is one, and otherwise on
          its own under the rail. Tool-only and terminated (failed/cancelled)
          turns have no answer bubble at all, and those are exactly the turns
          where this line is the only record of what happened, so it must not
          hang off `answerMsg`. */}
      {showCanvasAddSummary && canvasAddSummary && (
        <CanvasNodesAddedSummary summary={canvasAddSummary} />
      )}

      {/* The final answer stays visible regardless of rail state. */}
      {answerMsg && <ChatBubble message={answerMsg} entities={entities} />}
    </div>
  );
}
